/* Shared helpers for the personal rental signing pages:
   - sections/rental-agreement-page.liquid (assets/rental-agreement-page.js)
   - sections/credit-account-application-page.liquid
     (assets/credit-account-application-page.js)
   Loaded (deferred) before either page script; exposes window.MCSRentalSigning. */
(function () {
  'use strict';

  /* The rental agreement's personal token is kept under this key for
     reloads. (The credit account application keeps its own copy under
     'mcs-credit-application-token'.) */
  var TOKEN_STORE_KEY = 'mcs-rental-agreement-token';

  var str = function (value) { return value == null ? '' : String(value).trim(); };
  var escapeHtml = function (value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var joinList = function (items) {
    if (items.length <= 1) return items.join('');
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  };
  var isIsoDate = function (value) { return /^\d{4}-\d{2}-\d{2}$/.test(str(value)); };
  /* Same format as the signed PDF ("14 October 2026"). T00:00:00 keeps the
     date local (a bare YYYY-MM-DD parses as UTC). */
  var longDate = function (iso) {
    if (!isIsoDate(iso)) return '';
    return new Date(str(iso) + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  var todayLong = function () {
    return new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  /* "45" / "45.5" / "$45.00" -> "$45.00"; anything non-numeric shows as sent. */
  var money = function (value) {
    var raw = str(value);
    if (!raw) return '';
    var n = Number(raw.replace(/[$,\s]/g, ''));
    return isFinite(n) ? '$' + n.toFixed(2) : raw;
  };

  /* Plausibility guards (same rules as the trial page). */
  var isValidAbn = function (value) {
    /* ATO checksum: 11 digits, first digit minus 1, weighted sum % 89. */
    var digits = String(value || '').replace(/\D/g, '');
    if (digits.length !== 11) return false;
    var weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    var sum = 0;
    for (var i = 0; i < 11; i += 1) {
      sum += (Number(digits.charAt(i)) - (i === 0 ? 1 : 0)) * weights[i];
    }
    return sum % 89 === 0;
  };
  var looksLikeName = function (value) {
    return /[A-Za-z][\s\S]*[A-Za-z]/.test(String(value || ''));
  };
  var looksLikePhone = function (value) {
    var digits = String(value || '').replace(/\D/g, '');
    return digits.length >= 8 && digits.length <= 12 && !/^(\d)\1+$/.test(digits);
  };
  var looksLikeEmail = function (value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());
  };
  var passesLuhn = function (value) {
    var digits = String(value || '').replace(/\D/g, '');
    if (digits.length < 12 || digits.length > 19) return false;
    var sum = 0;
    for (var i = 0; i < digits.length; i += 1) {
      var d = Number(digits.charAt(digits.length - 1 - i));
      if (i % 2 === 1) {
        d *= 2;
        if (d > 9) d -= 9;
      }
      sum += d;
    }
    return sum % 10 === 0;
  };

  var reduceMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };
  var focusQuietly = function (el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (error) { el.focus(); }
  };

  var readConfig = function (root) {
    try {
      var el = root.querySelector('[data-ra-config]');
      return JSON.parse((el && el.textContent) || '{}') || {};
    } catch (error) {
      return {};
    }
  };
  /* {base}/{path or fallback}, tolerant of stray slashes. */
  var endpoint = function (baseUrl, path, fallback) {
    return str(baseUrl).replace(/\/+$/, '') + '/' + (str(path) || fallback).replace(/^\/+/, '');
  };

  /* The token is a personal credential: read it once, keep it for reloads
     in sessionStorage and take it out of the address bar so it does not
     linger in history, shared screenshots or analytics page URLs. storeKey
     defaults to the agreement's key; the credit account application uses
     its own so a stale rental token never attaches to a standalone
     application. */
  var readToken = function (storeKey) {
    var key = storeKey || TOKEN_STORE_KEY;
    var token = '';
    try {
      var params = new URLSearchParams(window.location.search);
      /* snippets/mcs-url-secrets.liquid has usually lifted it out of the URL already. */
      token = str((window.__mcsUrlSecrets || {}).t) || str(params.get('t'));
      if (token) {
        try { window.sessionStorage.setItem(key, token); } catch (storeError) { /* private mode */ }
        params.delete('t');
        var rest = params.toString();
        window.history.replaceState(window.history.state, '',
          window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
      } else {
        try { token = str(window.sessionStorage.getItem(key)); } catch (storeError) { token = ''; }
      }
    } catch (error) {
      token = '';
    }
    return token;
  };
  var forgetToken = function (storeKey) {
    try { window.sessionStorage.removeItem(storeKey || TOKEN_STORE_KEY); } catch (error) { /* best-effort */ }
  };

  /* One visible [data-ra-state] panel at a time; root[data-ra-current] names
     it. A .rtp-confirm panel gets its entrance transition. */
  var createStates = function (root, onShow) {
    return function (name, moveFocus) {
      Array.prototype.slice.call(root.querySelectorAll('[data-ra-state]')).forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-ra-state') !== name;
      });
      root.setAttribute('data-ra-current', name);
      if (onShow) onShow(name);
      var panel = root.querySelector('[data-ra-state="' + name + '"]');
      if (!panel) return;
      if (panel.classList.contains('rtp-confirm')) {
        void panel.offsetWidth;
        panel.classList.add('is-visible');
      }
      if (moveFocus) {
        focusQuietly(panel.querySelector('[data-ra-focus]') || panel);
        if (panel.scrollIntoView) panel.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    };
  };

  /* "Signed on Wednesday 8 October 2026." into [data-ra-signed-date]. */
  var paintSignedDate = function (el, signedAt) {
    if (!el) return;
    var when = signedAt ? new Date(signedAt) : null;
    if (when && !isNaN(when.getTime())) {
      el.textContent = 'Signed on ' + when.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) + '.';
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  };

  var statusBox = function (el) {
    return {
      set: function (message, tone, details) {
        if (!el) return;
        el.classList.remove('rtp-status--success', 'rtp-status--error');
        if (tone === 'success' || tone === 'error') el.classList.add('rtp-status--' + tone);
        var html = escapeHtml(message);
        if (details && details.length) {
          html += '<ul>' + details.map(function (line) { return '<li>' + escapeHtml(line) + '<\/li>'; }).join('') + '<\/ul>';
        }
        el.innerHTML = html;
        el.hidden = false;
      },
      clear: function () {
        if (!el) return;
        el.textContent = '';
        el.hidden = true;
        el.classList.remove('rtp-status--success', 'rtp-status--error');
      }
    };
  };

  /* Inline field errors under the input (inside its .ra-row__value). */
  var errorWrap = function (input) {
    return input.closest('.ra-row__value') || input.closest('.field') || input.parentNode;
  };
  var setFieldError = function (input, message) {
    if (!input) return;
    var wrap = errorWrap(input);
    var el = wrap.querySelector('.ra-field-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'ra-field-error';
      el.id = (input.id || input.name) + '-error';
      wrap.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', el.id);
  };
  var clearFieldError = function (input) {
    if (!input || !input.getAttribute || !input.closest) return;
    var el = errorWrap(input).querySelector('.ra-field-error');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  };

  /* Signature pad: pointer drawing; `state.drawn` turns true only once an
     actual stroke is made (state.onChange fires on draw/clear). Strokes map
     through the pad's size at set-up, so a later resize scales the drawing
     instead of misplacing new strokes. Call it once the canvas is visible. */
  var initSignaturePad = function (canvas, state, height) {
    state.drawn = false;
    var ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
    var pad = { clear: function () { state.drawn = false; }, ready: !!ctx };
    if (!ctx) return pad;
    var scale = window.devicePixelRatio || 1;
    var cssWidth = canvas.offsetWidth || 600;
    var cssHeight = height || canvas.offsetHeight || 120;
    /* A zero-sized canvas would export a blank PNG the backend rejects. */
    canvas.width = cssWidth * scale;
    canvas.height = cssHeight * scale;
    ctx.scale(scale, scale);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e2b4d';
    var drawing = false;
    var last = null;
    var pos = function (e) {
      var r = canvas.getBoundingClientRect();
      var sx = r.width ? cssWidth / r.width : 1;
      var sy = r.height ? cssHeight / r.height : 1;
      return { x: ((e.clientX || 0) - r.left) * sx, y: ((e.clientY || 0) - r.top) * sy };
    };
    canvas.addEventListener('pointerdown', function (e) {
      drawing = true;
      last = pos(e);
      if (canvas.setPointerCapture && e.pointerId !== undefined) {
        try { canvas.setPointerCapture(e.pointerId); } catch (error) { /* synthetic events */ }
      }
      e.preventDefault();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!drawing) return;
      var p = pos(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last = p;
      if (!state.drawn) {
        state.drawn = true;
        if (state.onChange) state.onChange();
      }
      e.preventDefault();
    });
    ['pointerup', 'pointercancel'].forEach(function (type) {
      canvas.addEventListener(type, function () { drawing = false; });
    });
    pad.clear = function () {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      state.drawn = false;
      if (state.onChange) state.onChange();
    };
    return pad;
  };

  /* fetch + JSON body, never rejecting on a bad body. Resolves
     { response, result }. */
  var fetchJson = function (url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (result) {
        return { response: response, result: result || {} };
      });
    });
  };

  window.MCSRentalSigning = {
    TOKEN_STORE_KEY: TOKEN_STORE_KEY,
    str: str,
    escapeHtml: escapeHtml,
    joinList: joinList,
    longDate: longDate,
    todayLong: todayLong,
    money: money,
    isValidAbn: isValidAbn,
    looksLikeName: looksLikeName,
    looksLikePhone: looksLikePhone,
    looksLikeEmail: looksLikeEmail,
    passesLuhn: passesLuhn,
    reduceMotion: reduceMotion,
    focusQuietly: focusQuietly,
    readConfig: readConfig,
    endpoint: endpoint,
    readToken: readToken,
    forgetToken: forgetToken,
    createStates: createStates,
    paintSignedDate: paintSignedDate,
    statusBox: statusBox,
    setFieldError: setFieldError,
    clearFieldError: clearFieldError,
    initSignaturePad: initSignaturePad,
    fetchJson: fetchJson
  };
})();
